// ChatPanel — the docked right panel (redesign "Chattpanel"), pure.
//
// Two views over one body. "Assistant" (the conversation) carries the design's
// head (f-01/b-05): a muted "Conversation" label with ONE quiet action at its
// end, collapse (the pen's double chevron down) that closes the panel, over
// the picker + "New" (a caller slot), then the thread. "Context" (what the chat made and the sources behind
// it, or one focused material/source; reached by "show it next to the chat"
// and the omnibar) shows the E-04 tab row instead: "Assistant" to go back, and
// the context's own tab named by what it shows — the focused material or
// source, else "Context" — with × to leave it.
//
// Pure/props-driven: the shown view and every intent are controlled; the
// body and the assistant's head are caller slots.

export type { ChatPanelView };
export { ChatPanel };

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Icon } from "../Icon/Icon.js";
import { IconButton } from "../IconButton/IconButton.js";
import { Tooltip } from "../Tooltip/Tooltip.js";
import "./ChatPanel.css";

type ChatPanelView = "assistant" | "context";

type ChatPanelProps = {
    readonly view: ChatPanelView;
    // The Context tab's name: the focused entity's title, else "Context".
    readonly contextLabel: string;
    readonly onShowView: (view: ChatPanelView) => void;
    readonly onClose: () => void;
    // Shown under the label on the Assistant view (the picker + "New").
    readonly head?: ReactNode;
    readonly children: ReactNode;
};

const ChatPanel = ({
    view,
    contextLabel,
    onShowView,
    onClose,
    head,
    children,
}: ChatPanelProps): ReactNode => {
    const { t } = useLingui();
    const collapsePanel = t`Collapse panel`;
    return (
        <section className="bilda-chat-panel" aria-label={t`Chat panel`}>
            {view === "assistant" ? (
                <div className="bilda-chat-panel__top">
                    <div className="bilda-chat-panel__label-row">
                        <span className="bilda-chat-panel__label">
                            {t`Conversation`}
                        </span>
                        <Tooltip content={collapsePanel}>
                            <IconButton
                                label={collapsePanel}
                                variant="ghost"
                                size="sm"
                                onClick={onClose}
                            >
                                <Icon name="chevrons-down" size="sm" />
                            </IconButton>
                        </Tooltip>
                    </div>
                    {head}
                </div>
            ) : (
                <div className="bilda-chat-panel__tabs">
                    <button
                        type="button"
                        className="bilda-chat-panel__tab"
                        onClick={() => onShowView("assistant")}
                    >
                        <Icon name="sparkle" size="sm" />
                        {t`Assistant`}
                    </button>
                    <span
                        className="bilda-chat-panel__tab"
                        data-active="true"
                        title={contextLabel}
                    >
                        <Icon name="panel" size="sm" />
                        <span className="bilda-chat-panel__tab-label">
                            {contextLabel}
                        </span>
                        <button
                            type="button"
                            className="bilda-chat-panel__tab-close"
                            aria-label={t`Back to the assistant`}
                            onClick={() => onShowView("assistant")}
                        >
                            <Icon name="close" size="xs" />
                        </button>
                    </span>
                </div>
            )}
            <div className="bilda-chat-panel__body">{children}</div>
        </section>
    );
};
